import { useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/router";
import type { TRPCClientErrorLike } from "@trpc/client";
import type { AppRouter } from "@remi/api";
import type { SentryMetricCounter } from "@remi/enums/sentry-metric-counter";
import { RemiErrorDeprecated } from "@remi/error";
import { remiLogger } from "@remi/logging";
import { reportCustomError } from "@remi/sentry";

import { useUserProfile } from "~/contexts/UserProfileContext";

type ErrorProps = {
  status: number;
  title?: string;
  message: React.ReactNode;
  actions?: React.ReactNode;
  resourceId?: number | string;
};

const TITLE: Record<number, string> = {
  401: "Unauthorized",
  404: "Not Found",
  500: "Something Went Wrong",
};

const LOGGABLE_HTTP_STATUSES = new Set([401, 403, 404, 409, 412, 422, 429, 503]);

const METRIC_HTTP_STATUSES: Record<number, SentryMetricCounter> = {
  401: "http.authenticated.status.401",
  403: "http.authenticated.status.403",
  404: "http.authenticated.status.404",
} as const;

const STATUS_LABEL: Record<number, string> = {
  401: "401 Unauthorized",
  403: "403 Forbidden",
  404: "404 Not Found",
  409: "409 Conflict",
  412: "412 Precondition Failed",
  422: "422 Unprocessable Entity",
  429: "429 Too Many Requests",
  503: "503 Service Unavailable",
};

export default function ErrorPage({ status, title, message, actions, resourceId }: ErrorProps) {
  const router = useRouter();
  const { user } = useUserProfile();
  const lastLoggedSignature = useRef<string | null>(null);
  const statusLabel = useMemo(() => STATUS_LABEL[status] ?? `HTTP ${status}`, [status]);
  const personaId = user.person.id;

  useEffect(() => {
    if (!LOGGABLE_HTTP_STATUSES.has(status)) {
      return;
    }
    const signatureParts = [String(status), router.asPath, resourceId !== undefined ? String(resourceId) : "null"];
    const signature = signatureParts.join("::");
    if (lastLoggedSignature.current === signature) {
      return;
    }

    // Skip anonymous contexts (e.g. public 404 page rendered outside providers)
    if (!personaId || personaId === "fake-user-for-profile-context") {
      return;
    }

    lastLoggedSignature.current = signature;

    const metricStatus = METRIC_HTTP_STATUSES[status];
    if (metricStatus) {
      remiLogger.captureCounterMetric(metricStatus, 1, {
        attributes: {
          path: router.asPath,
          personaId,
          ...(resourceId !== undefined && { resourceId: String(resourceId) }),
        },
      });
      return;
    }

    reportCustomError((withScope, captureException) => {
      withScope((scope) => {
        scope.setTag("http.status", String(status));
        scope.setTag("http.status_label", statusLabel);
        if (resourceId !== undefined) {
          scope.setTag("resource.id", String(resourceId));
        }
        captureException(new RemiErrorDeprecated({ message: `Unexpected authenticated ${statusLabel}` }));
      });
    });
  }, [personaId, resourceId, router.asPath, status, statusLabel]);

  return (
    <div className="grid min-h-full w-full place-items-center bg-background-primary px-6 py-24 sm:py-32 lg:px-8">
      <div className="text-center">
        <p className="font-semibold text-base text-primary">{status}</p>
        <h1 className="mt-4 font-bold text-3xl text-text-primary tracking-tight sm:text-5xl">
          {title ?? TITLE[status] ?? "Error"}
        </h1>
        <p className="mt-6 text-base text-text-tertiary leading-7">{message}</p>
        <div className="mt-10 flex items-center justify-center gap-x-6">{actions}</div>
      </div>
    </div>
  );
}

/**
 * Display an error page given a TRPC Client error
 */
export function TRPCError({ error }: { error: TRPCClientErrorLike<AppRouter> }) {
  const statusCode = error.data?.httpStatus ?? 500;
  return <ErrorPage status={statusCode} message={error.message} />;
}
